import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import i18next from 'i18next';

const L = (en, es) => ((i18next.language || 'es').startsWith('es') ? es : en);

const STAGE_TARGETS = [
  { value: 'findings', en: 'Findings (Stage 3 → 4)', es: 'Hallazgos (Etapa 3 → 4)' },
  { value: 'recommendations', en: 'Recommendations (Stage 4 → 5)', es: 'Recomendaciones (Etapa 4 → 5)' },
  { value: 'closure', en: 'Closure (Stage 5 → 6)', es: 'Cierre (Etapa 5 → 6)' },
];

const KIND_LABEL = {
  min_count:                    'Minimum count of entity',
  min_diversity:                'Minimum distinct evidence kinds',
  min_compliance_pct:           'Minimum compliance %',
  requires_completed:           'Requires N completed of entity',
  requires_outcome_anywhere:    'Requires outcome in allowed list',
  requires_evidence_family:     'Minimum evidence per family',
  no_open_mediations:           'No open mediations',
};

// ---------------------------------------------------------------------
// Row editor — one rule
// ---------------------------------------------------------------------
function RuleRow({ rule, onSaved, onDeleted, companyId }) {
  const isSystem = rule.scope === 'system';
  const [editing, setEditing] = useState(false);
  const [weight, setWeight] = useState(rule.weight);
  const [isBlocking, setIsBlocking] = useState(rule.is_blocking);
  const [isActive, setIsActive] = useState(rule.is_active);
  const [thresholdValue, setThresholdValue] = useState(rule.threshold?.value ?? '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const label = rule.label?.en || rule.key;
  const kind = rule.threshold?.kind || '—';

  const save = async () => {
    setSaving(true);
    try {
      if (isSystem) {
        // Cannot patch system row — create a company override
        const body = {
          company_id: companyId,
          stage_target: rule.stage_target,
          key: rule.key,
          label: rule.label,
          weight: Number(weight),
          threshold: { ...rule.threshold, value: typeof thresholdValue === 'number' ? thresholdValue : (Number(thresholdValue) || rule.threshold.value) },
          is_blocking: isBlocking,
          is_active: isActive,
        };
        await adminApi.tf6CreateRule(body);
        toast.success(L('Company override created','Regla específica de empresa creada'));
      } else {
        await adminApi.tf6PatchRule(rule.id, {
          weight: Number(weight),
          threshold: { ...rule.threshold, value: typeof thresholdValue === 'number' ? thresholdValue : (Number(thresholdValue) || rule.threshold.value) },
          is_blocking: isBlocking,
          is_active: isActive,
        });
        toast.success(L('Rule updated','Regla actualizada'));
      }
      setEditing(false);
      onSaved?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed');
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (isSystem) { toast.error(L('System rules cannot be deleted — deactivate instead','Las reglas de sistema no se pueden eliminar — desactívalas en su lugar')); return; }
    if (!window.confirm(`Delete override for "${label}"?`)) return;
    setDeleting(true);
    try {
      await adminApi.tf6DeleteRule(rule.id);
      toast.success(L('Override deleted (system default restored)','Regla eliminada (se restauró el valor de sistema)'));
      onDeleted?.();
    } catch (e) {
      toast.error(L('Delete failed','No se pudo eliminar'));
    } finally { setDeleting(false); }
  };

  return (
    <Card data-testid={`tf6-rule-editor-row-${rule.key}`} className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-slate-800 truncate">{label}</p>
              <Badge variant={isSystem ? 'outline' : 'default'} className="text-xs">
                {isSystem ? 'system' : 'company override'}
              </Badge>
              {!rule.is_active && <Badge variant="outline" className="text-xs text-slate-500">{L('deactivated','desactivada')}</Badge>}
            </div>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">{rule.key} · {KIND_LABEL[kind] || kind}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {editing ? (
              <>
                <Button size="sm" onClick={save} disabled={saving} data-testid={`tf6-rule-save-${rule.key}`}>
                  <Save className="h-3.5 w-3.5 mr-1" />{saving ? 'Saving…' : (isSystem ? 'Create override' : 'Save')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)}>{L('Cancel','Cancelar')}</Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`tf6-rule-edit-${rule.key}`}>
                  {isSystem ? L('Override','Personalizar') : L('Edit','Editar')}
                </Button>
                {!isSystem && (
                  <Button size="sm" variant="ghost" onClick={del} disabled={deleting}
                          data-testid={`tf6-rule-delete-${rule.key}`}>
                    <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <label className="text-xs text-slate-500">{L('Weight (1-100)','Peso (1-100)')}</label>
            <Input type="number" min="1" max="100" value={weight}
                   onChange={(e) => setWeight(e.target.value)} disabled={!editing}
                   data-testid={`tf6-rule-weight-${rule.key}`} />
          </div>
          <div>
            <label className="text-xs text-slate-500">{L('Threshold value','Valor umbral')}</label>
            <Input value={thresholdValue} onChange={(e) => setThresholdValue(e.target.value)} disabled={!editing}
                   data-testid={`tf6-rule-threshold-${rule.key}`} />
          </div>
          <div>
            <label className="text-xs text-slate-500">{L('Blocking','Bloqueante')}</label>
            <div className="flex items-center h-9">
              <Switch checked={!!isBlocking} onCheckedChange={setIsBlocking} disabled={!editing}
                      data-testid={`tf6-rule-blocking-${rule.key}`} />
              <span className="ml-2 text-xs text-slate-600">{isBlocking ? 'Yes' : 'No'}</span>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">{L('Active','Activa')}</label>
            <div className="flex items-center h-9">
              <Switch checked={!!isActive} onCheckedChange={setIsActive} disabled={!editing}
                      data-testid={`tf6-rule-active-${rule.key}`} />
              <span className="ml-2 text-xs text-slate-600">{isActive ? 'On' : 'Off'}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------
// New Custom Rule Dialog
// ---------------------------------------------------------------------
function NewRuleDialog({ open, onOpenChange, companyId, stageTarget, onCreated }) {
  const [form, setForm] = useState({
    key: '',
    labelEn: '',
    weight: 10,
    isBlocking: false,
    isActive: true,
    kind: 'min_count',
    entity: 'interviews',
    family: 'document',
    value: 1,
  });
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) setForm({ key: '', labelEn: '', weight: 10, isBlocking: false, isActive: true, kind: 'min_count', entity: 'interviews', family: 'document', value: 1 }); }, [open]);

  const submit = async () => {
    if (!form.key.trim() || !form.labelEn.trim()) { toast.error(L('Key and label required','La clave y la etiqueta son obligatorias')); return; }
    setSubmitting(true);
    try {
      const threshold = { kind: form.kind, value: Number(form.value) };
      if (form.kind === 'min_count' || form.kind === 'requires_completed') threshold.entity = form.entity;
      if (form.kind === 'requires_evidence_family') threshold.family = form.family;
      if (form.kind === 'requires_outcome_anywhere') {
        threshold.values = ['resolved', 'partially_resolved'];
        threshold.entity = 'mediation';
        delete threshold.value;
      }
      await adminApi.tf6CreateRule({
        company_id: companyId,
        stage_target: stageTarget,
        key: form.key.trim(),
        label: { en: form.labelEn.trim() },
        weight: Number(form.weight),
        threshold,
        is_blocking: form.isBlocking,
        is_active: form.isActive,
      });
      toast.success(L('Rule created','Regla creada'));
      onOpenChange(false); onCreated?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Create failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf6-new-rule-dialog">
        <DialogHeader>
          <DialogTitle>{L('New company rule','Nueva regla de empresa')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">{L('Key (unique)','Clave (única)')}</label>
              <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })}
                     placeholder="custom_eg_min_legal_docs" data-testid="tf6-new-rule-key" />
            </div>
            <div>
              <label className="text-xs text-slate-600">{L('Weight','Peso')}</label>
              <Input type="number" min="1" max="100" value={form.weight}
                     onChange={(e) => setForm({ ...form, weight: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-600">{L('Label (English)','Etiqueta (inglés)')}</label>
            <Input value={form.labelEn} onChange={(e) => setForm({ ...form, labelEn: e.target.value })}
                   placeholder={L('At least 2 legal documents on file','Al menos 2 documentos legales archivados')} />
          </div>
          <div>
            <label className="text-xs text-slate-600">{L('Threshold kind','Tipo de umbral')}</label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(KIND_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(form.kind === 'min_count' || form.kind === 'requires_completed') && (
            <div>
              <label className="text-xs text-slate-600">{L('Entity','Entidad')}</label>
              <Select value={form.entity} onValueChange={(v) => setForm({ ...form, entity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="interviews">interviews</SelectItem>
                  <SelectItem value="observations">observations</SelectItem>
                  <SelectItem value="mediations">mediations</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {form.kind === 'requires_evidence_family' && (
            <div>
              <label className="text-xs text-slate-600">{L('Evidence family','Familia de evidencia')}</label>
              <Select value={form.family} onValueChange={(v) => setForm({ ...form, family: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="document">document</SelectItem>
                  <SelectItem value="media">media</SelectItem>
                  <SelectItem value="testimony">testimony</SelectItem>
                  <SelectItem value="communication">communication</SelectItem>
                  <SelectItem value="snapshot">snapshot</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {form.kind !== 'requires_outcome_anywhere' && form.kind !== 'no_open_mediations' && (
            <div>
              <label className="text-xs text-slate-600">{L('Threshold value (number)','Valor umbral (número)')}</label>
              <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </div>
          )}
          <div className="flex items-center gap-6">
            <label className="inline-flex items-center gap-2 text-xs text-slate-600">
              <Switch checked={form.isBlocking} onCheckedChange={(v) => setForm({ ...form, isBlocking: v })} />
              Blocking
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-slate-600">
              <Switch checked={form.isActive} onCheckedChange={(v) => setForm({ ...form, isActive: v })} />
              Active
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="tf6-new-rule-submit">
            {submitting ? 'Creating…' : 'Create rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------
export default function AdminTalentFixSufficiencyRules() {
  const [params, setParams] = useSearchParams();
  const { user } = useApp();
  const isHmAdmin = user?.role === 'hm_admin';
  const initialCompanyId = params.get('company_id') || '';
  const initialStageTarget = params.get('stage_target') || 'findings';

  const [companyId, setCompanyId] = useState(initialCompanyId);
  const [stageTarget, setStageTarget] = useState(initialStageTarget);
  const [companies, setCompanies] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newRuleOpen, setNewRuleOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Load company list (admin can pick any)
  useEffect(() => {
    adminApi.listCompanies({ book: 'all' })
      .then((arr) => {
        setCompanies(arr || []);
        if (!companyId && arr?.length) {
          const first = arr[0].id;
          setCompanyId(first);
          setParams({ company_id: first, stage_target: stageTarget }, { replace: true });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await adminApi.tf6ListRules({ company_id: companyId, stage_target: stageTarget });
      setRules(res.items || []);
    } catch (e) {
      toast.error(L('Could not load rules','No se pudieron cargar las reglas'));
    } finally { setLoading(false); }
  }, [companyId, stageTarget]);

  useEffect(() => { load(); }, [load]);

  // Keep URL in sync
  useEffect(() => {
    if (companyId) {
      setParams({ company_id: companyId, stage_target: stageTarget }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, stageTarget]);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const res = await adminApi.tf6SeedDefaults();
      toast.success(`Seed complete — ${res.inserted} new rule(s)`);
      load();
    } catch (e) {
      toast.error(L('Seed failed','No se pudo inicializar'));
    } finally { setSeeding(false); }
  };

  if (!isHmAdmin) {
    return (
      <div className="p-6">
        <p className="text-sm text-rose-600">{L('Only HM Admin users can configure sufficiency rules.','Solo los usuarios HM Admin pueden configurar las reglas de suficiencia.')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="tf6-rules-page">
      <div>
        <Link to="/admin/talentfix" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" />Back to TalentFix
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">{L('Sufficiency Rules','Reglas de Suficiencia')}</h1>
        <p className="text-sm text-slate-500">{L('Configure per-company thresholds. System defaults shown unless overridden.','Configura umbrales por empresa. Se muestran los valores de sistema salvo que existan reglas específicas.')}</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{L('Scope','Alcance')}</CardTitle>
          <CardDescription>{L('Choose company + stage target. Overrides only apply to the selected company.','Elige empresa + etapa objetivo. Las reglas personalizadas solo aplican a la empresa seleccionada.')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="text-xs text-slate-600">{L('Company','Empresa')}</label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger data-testid="tf6-rules-company-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-600">{L('Stage target','Etapa objetivo')}</label>
              <Select value={stageTarget} onValueChange={setStageTarget}>
                <SelectTrigger data-testid="tf6-rules-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGE_TARGETS.map((s) => <SelectItem key={s.value} value={s.value}>{L(s.en, s.es)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={() => setNewRuleOpen(true)} disabled={!companyId}
                      data-testid="tf6-new-rule-btn">
                <Plus className="h-4 w-4 mr-1" />{L('New rule','Nueva regla')}
              </Button>
              <Button variant="outline" onClick={seedDefaults} disabled={seeding}
                      data-testid="tf6-seed-defaults-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1" />{seeding ? L('Seeding…','Restableciendo…') : L('Re-seed defaults','Restablecer valores predeterminados')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm italic text-slate-500">Loading rules…</p>
      ) : rules.length === 0 ? (
        <p className="text-sm italic text-slate-500" data-testid="tf6-rules-empty">{L('No rules configured for this stage target.','No hay reglas configuradas para esta etapa objetivo.')}</p>
      ) : (
        <div className="space-y-3">
          {rules.map((r) => (
            <RuleRow key={r.id} rule={r} companyId={companyId} onSaved={load} onDeleted={load} />
          ))}
        </div>
      )}

      <NewRuleDialog open={newRuleOpen} onOpenChange={setNewRuleOpen}
        companyId={companyId} stageTarget={stageTarget} onCreated={load} />
    </div>
  );
}
